import { FC, FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  useLazyAuthLoginQuery,
  useResendMfaCodeMutation,
  useVerifyMfaCodeMutation,
} from '@store/rtk/auth';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  selectUsers,
  setUserInfo,
  setUserType,
} from '@store/slices/users/usersSlice';
import { homePathForVersions, WEB_USER } from '@routes/helpers';
import {
  clearStoredChallenge,
  getStoredChallenge,
  updateStoredChallenge,
} from './constants';
import { VerificationCodeLayout } from './VerificationCodeLayout';
import { STORAGE_VERSION_KEY } from '@page/ConfigWrapper';
import { toastError } from '@utils/helpers';
import { toastMessage } from '@utils/toastMessage';

const ACCOUNT_LOCKED_STATUS = 423;

export const EnterVerificationCode: FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { userType } = useAppSelector(selectUsers);

  const challenge = getStoredChallenge();
  const [code, setCode] = useState('');
  const verifiedRef = useRef(false);

  const [verifyMfaCode, { isLoading: isVerifying }] =
    useVerifyMfaCodeMutation();
  const [resendMfaCode, { isLoading: isResending }] =
    useResendMfaCodeMutation();
  const [getUserInfo] = useLazyAuthLoginQuery();

  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (verifiedRef.current) return;
    if (!challenge?.challengeToken) {
      navigate('/authorization/login', { replace: true });
      return;
    }
    if (!challenge.maskedContact) {
      navigate('/authorization/get-verification-code', { replace: true });
    }
  }, [challenge?.challengeToken, challenge?.maskedContact, navigate]);

  useEffect(() => {
    if (!challenge?.expiresAt) return undefined;
    // Backend returns expiresAt without a timezone suffix but it's UTC.
    const expiresAt = new Date(
      challenge.expiresAt.endsWith('Z')
        ? challenge.expiresAt
        : `${challenge.expiresAt}Z`
    ).getTime();

    const check = () => setIsExpired(Date.now() >= expiresAt);
    check();
    const interval = window.setInterval(check, 1000);
    return () => window.clearInterval(interval);
  }, [challenge?.expiresAt]);

  if (!challenge?.challengeToken || !challenge.maskedContact) return null;

  const handleSubmitCode = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!code || isExpired) return;

    try {
      await verifyMfaCode({
        challengeToken: challenge.challengeToken,
        code,
      }).unwrap();

      verifiedRef.current = true;
      clearStoredChallenge();

      const userResult = await getUserInfo({}).unwrap();
      dispatch(setUserInfo(userResult));
      dispatch(setUserType(userResult.userType ?? WEB_USER));
      localStorage.setItem(
        STORAGE_VERSION_KEY,
        userResult.userType ?? WEB_USER
      );
      navigate(homePathForVersions[userType], { replace: true });
    } catch (err: any) {
      if (err?.status === ACCOUNT_LOCKED_STATUS) {
        clearStoredChallenge();
        toast.error(toastMessage.AUTH.accountLockedTooManyAttempts);
        navigate('/authorization/login', { replace: true });
        return;
      }

      toastError(err, toastMessage.AUTH.incorrectVerificationCode);
      setCode('');
    }
  };

  const handleResend = async () => {
    try {
      const result = await resendMfaCode({
        challengeToken: challenge.challengeToken,
      }).unwrap();

      updateStoredChallenge({
        challengeToken: result.challengeToken,
        method: result.method,
        maskedContact: result.maskedContact,
        expiresAt: result.expiresAt,
        remainingCodeRequests: result.remainingCodeRequests,
      });
      setCode('');
      toast.success(toastMessage.AUTH.verificationCodeResent);
    } catch (err: any) {
      toastError(err, toastMessage.AUTH.unableToResendCode);
    }
  };

  const handleCancel = () => {
    clearStoredChallenge();
    navigate('/authorization/login');
  };

  const resendDisabled =
    isResending ||
    (challenge.remainingCodeRequests !== undefined &&
      challenge.remainingCodeRequests <= 0);

  return (
    <VerificationCodeLayout>
      <form className="verification-code__form" onSubmit={handleSubmitCode}>
        <h1>Enter Verification Code</h1>

        <div className="verification-code__message">
          <p>We sent you a verification code.</p>
          <strong>{challenge.maskedContact}</strong>
        </div>

        <label className="verification-code__field">
          <span>Verification Code</span>
          <input
            inputMode="numeric"
            maxLength={6}
            value={code}
            placeholder="123456"
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
          />
        </label>

        <p className="verification-code__hint">
          {isExpired
            ? 'This code has expired. Request a new one below.'
            : 'The code expires in 10 minutes after you request it.'}
        </p>

        <button
          className="verification-code__link"
          type="button"
          onClick={handleResend}
          disabled={resendDisabled}
        >
          Get another code
        </button>

        <div className="verification-code__actions">
          <button
            className="verification-code__button"
            type="submit"
            disabled={!code || isExpired || isVerifying}
          >
            Submit
          </button>
          <button
            className="verification-code__button verification-code__button--cancel"
            type="button"
            onClick={handleCancel}
          >
            Cancel
          </button>
        </div>
      </form>
    </VerificationCodeLayout>
  );
};
